/**
 * The lazy daśā period tree
 *
 * mahā → antar → pratyantar → sūkṣma (→ prāṇa → deha), for every daśā system.
 * This is the one thing the nine systems genuinely share; their period *rules*
 * share almost nothing (see common/plans/07-dashas.md), so those stay in
 * their own modules and hand this file two small functions:
 *
 *   `rootItems`  — the top-level periods, in order, each with a length in days;
 *   `childItems` — given a period, its sub-periods as weights, or `null`.
 *
 * ── Two rules this file enforces ────────────────────────────────────────────
 *
 * ⚠ **Everything is a Julian Day until the moment it is printed.** A sub-period
 * is `parent.start + parent.span × cumulativeWeight / totalWeight`, measured from
 * the *parent's* start, never from the previous sibling. Chaining "365.25 days
 * per year" through four levels would accumulate days of drift; measuring each
 * child against its parent cannot, and the last child ends on exactly the
 * parent's end — so sub-periods sum to the parent to the last bit, not merely
 * to the day.
 *
 * ⚠ **Nothing below the requested row is built.** A century of sūkṣma periods is
 * 9⁴ = 6,561 rows per cycle; rendering one row must not create them. Children are
 * made on first request and remembered per parent. `at()` descends one path,
 * `range()` descends only into overlapping parents.
 *
 * ── What a "year" is ────────────────────────────────────────────────────────
 *
 * ◆ **`YEAR_DAYS.solar` = 365.2425 d is the default, everywhere, at every level.**
 * It is the Gregorian mean year, which is within 26 seconds of the mean tropical
 * year, and it is one constant — not 365.25 at one level and 365.2425 at another.
 * The choice is a setting because the śāstra does not settle it (BPHS / Parāśara §16: he
 * himself uses sāvana years, 360 d, "not meant to be a recommendation"; most
 * scholars use solar). The two differ by 1.4 %, which is 19 days in a 20-year
 * Śukra daśā. Every tree reports the basis it used in `tree.info.yearDays`.
 *
 * ⚠ the classical *rāśi* daśās measure time by the Sun's arc (§18.6: a "year" is 360° of
 * solar motion, a "month" 30°, a "day" 1°) so that a daśā ends when the Sun
 * returns to its birth longitude. This module uses the uniform mean year for
 * those too; the difference is at most a few days inside a year and nil at
 * whole-year boundaries. Recorded as an open item, not silently ignored.
 *
 * © Manish Jagdish Thatte
 */

export const YEAR_DAYS = Object.freeze({
  solar: 365.2425,     // default — Gregorian mean year
  julian: 365.25,
  sidereal: 365.256363,
  savana: 360          // a classical choice throughout his book
});

export const DEFAULT_YEAR_BASIS = 'solar';
export const DEFAULT_YEAR_DAYS = YEAR_DAYS[DEFAULT_YEAR_BASIS];

/** Level names, 1-based: `LEVEL_NAMES[level - 1]`. */
export const LEVEL_NAMES = Object.freeze([
  { en: 'Mahādaśā', short: 'Mahā' },
  { en: 'Antardaśā', short: 'Antar' },
  { en: 'Pratyantardaśā', short: 'Pratyantar' },
  { en: 'Sūkṣmadaśā', short: 'Sūkṣma' },
  { en: 'Prāṇadaśā', short: 'Prāṇa' },
  { en: 'Dehadaśā', short: 'Deha' }
]);

export const DEFAULT_MAX_LEVEL = 4;

/** Resolve `yearDays` from a number or one of the `YEAR_DAYS` names. */
export function resolveYearDays(value) {
  if (value === undefined || value === null) return DEFAULT_YEAR_DAYS;
  if (typeof value === 'string') {
    if (!(value in YEAR_DAYS)) {
      throw new RangeError(`unknown year basis "${value}" — expected one of ${Object.keys(YEAR_DAYS).join(', ')}`);
    }
    return YEAR_DAYS[value];
  }
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`yearDays must be a positive number, got ${value}`);
  }
  return value;
}

/** The name of a year basis for a number of days, or null for a custom value. */
export function yearBasisName(yearDays) {
  return Object.keys(YEAR_DAYS).find((k) => YEAR_DAYS[k] === yearDays) ?? null;
}

/**
 * Pull the two inputs every daśā needs out of whatever the caller has.
 *
 * Accepts a kundali (`grahas.moon.lon`, `input.jdUT`) or the bare
 * `{ moonLon, birthJD }`. ⚠ `moonLon` is **sidereal** — nakshatra.js says why.
 */
export function birthFrom(input) {
  const moonLon = input?.moonLon ?? input?.grahas?.moon?.lon;
  const birthJD = input?.birthJD ?? input?.input?.jdUT;
  if (!Number.isFinite(moonLon)) {
    throw new TypeError('daśā: the Moon\'s sidereal longitude is required (kundali.grahas.moon.lon or moonLon)');
  }
  if (!Number.isFinite(birthJD)) {
    throw new TypeError('daśā: the birth Julian Day (UT) is required (kundali.input.jdUT or birthJD)');
  }
  return { moonLon, birthJD };
}

/**
 * Build a lazy period tree.
 *
 * `rootItems`: `[{ key, days, ...data }]`, laid end to end from `startJD`.
 * `childItems(period, tree)`: `[{ key, weight, ...data }]` or `null`/`[]` for a leaf.
 * `startJD` may precede `birthJD` — a nakṣatra daśā begins *before* birth by the
 * part of the first period already elapsed. Those periods carry
 * `startsBeforeBirth` so the UI can say "balance at birth" rather than show a
 * start date nobody lived through.
 */
export function createPeriodTree({
  system,
  name = system,
  birthJD,
  startJD,
  yearDays = DEFAULT_YEAR_DAYS,
  rootItems,
  childItems,
  maxLevel = DEFAULT_MAX_LEVEL,
  info = {}
}) {
  if (!Number.isFinite(birthJD) || !Number.isFinite(startJD)) {
    throw new TypeError('createPeriodTree: birthJD and startJD must be finite');
  }
  if (!rootItems?.length) throw new RangeError('createPeriodTree: a daśā needs at least one period');

  const cache = new Map();   // period.id → children array
  const childNotes = new Map(); // period.id → what decided the sub-sequence
  let built = 0;

  const make = (level, parent, item, start, end, index) => {
    built += 1;
    const path = parent ? [...parent.path, item.key] : [item.key];
    const { key, days, weight, ...data } = item;
    return Object.freeze({
      system,
      // ⚠ The index is part of the id: Kālacakra and the rāśi daśās repeat a
      // rāśi among siblings, and two periods must never share an id.
      id: `${parent ? parent.id : system}/${key}#${index}`,
      index,
      level,
      levelName: LEVEL_NAMES[level - 1]?.short ?? `L${level}`,
      key,
      path: Object.freeze(path),
      startJD: start,
      endJD: end,
      days: end - start,
      years: (end - start) / yearDays,
      startsBeforeBirth: start < birthJD && end > birthJD,
      beforeBirth: end <= birthJD,
      data: Object.freeze(data)
    });
  };

  // Roots are laid out from the start; positions are `startJD + cumulative`
  // computed from the start each time, not by chaining ends.
  const roots = [];
  {
    let cum = 0;
    const total = rootItems.reduce((a, r) => a + r.days, 0);
    rootItems.forEach((item, i) => {
      const start = startJD + cum;
      cum += item.days;
      const end = i === rootItems.length - 1 ? startJD + total : startJD + cum;
      roots.push(make(1, null, item, start, end, i));
    });
  }
  Object.freeze(roots);

  const tree = {
    system,
    name,
    birthJD,
    startJD,
    endJD: roots[roots.length - 1].endJD,
    yearDays,
    maxLevel,
    roots,
    info: Object.freeze({ yearDays, yearBasis: yearBasisName(yearDays), ...info }),

    /** Sub-periods of a period; built on first request, then remembered. */
    children(period) {
      if (period.level >= maxLevel) return [];
      const hit = cache.get(period.id);
      if (hit) return hit;

      const items = childItems ? childItems(period, tree) : null;
      let out = [];
      if (items?.length) {
        const total = items.reduce((a, c) => a + c.weight, 0);
        if (!(total > 0)) throw new RangeError(`${system}: sub-period weights of ${period.id} sum to ${total}`);
        let cum = 0;
        out = items.map((item, i) => {
          const start = period.startJD + (period.days * cum) / total;
          cum += item.weight;
          const end = i === items.length - 1
            ? period.endJD
            : period.startJD + (period.days * cum) / total;
          return make(period.level + 1, period, item, start, end, i);
        });
      }
      Object.freeze(out);
      cache.set(period.id, out);
      return out;
    },

    /** Why this period's sub-sequence starts where it does (rāśi daśās). */
    childNote(period) {
      return childNotes.get(period.id) ?? null;
    },
    _setChildNote(period, note) { childNotes.set(period.id, note); },

    /**
     * The chain of periods running at `jd`, from level 1 down to `depth`.
     * Empty when `jd` is outside the tree. Half-open: a period owns its start
     * instant and not its end, so a boundary belongs to the later period.
     */
    at(jd, depth = maxLevel) {
      if (!(jd >= tree.startJD && jd < tree.endJD)) return [];
      const chain = [];
      let level = roots;
      for (let l = 1; l <= Math.min(depth, maxLevel); l++) {
        const hit = level.find((p) => jd >= p.startJD && jd < p.endJD);
        if (!hit) break;
        chain.push(hit);
        if (l === depth) break;
        level = tree.children(hit);
        if (!level.length) break;
      }
      return chain;
    },

    /** Periods at exactly `depth` that overlap `[jdFrom, jdTo)`. Only overlapping parents are opened. */
    range(jdFrom, jdTo, depth = 2) {
      const out = [];
      const visit = (list, l) => {
        for (const p of list) {
          if (p.endJD <= jdFrom || p.startJD >= jdTo) continue;
          if (l === depth) out.push(p);
          else if (l < maxLevel) visit(tree.children(p), l + 1);
        }
      };
      visit(roots, 1);
      return out;
    },

    /** How many period objects exist right now — the test that laziness is real. */
    materialised() { return built; },

    /** Plain-object summary for tests and export; expands only what is asked. */
    toJSON(depth = 1) {
      const dump = (p) => ({
        key: p.key, level: p.level, startJD: p.startJD, endJD: p.endJD,
        ...(p.level < depth ? { children: tree.children(p).map(dump) } : {})
      });
      return { system, info: tree.info, periods: roots.map(dump) };
    }
  };

  return Object.freeze(tree);
}

/**
 * Check that a list of sibling periods is contiguous and tiles its parent.
 * Returns `{ ok, gaps, overlaps }` — used by the tests for every system.
 */
export function checkContiguous(periods, parent = null, epsilon = 1e-9) {
  const gaps = [];
  const overlaps = [];
  for (let i = 1; i < periods.length; i++) {
    const d = periods[i].startJD - periods[i - 1].endJD;
    if (d > epsilon) gaps.push({ at: i, days: d });
    else if (d < -epsilon) overlaps.push({ at: i, days: -d });
  }
  let edge = null;
  if (parent && periods.length) {
    const s = periods[0].startJD - parent.startJD;
    const e = periods[periods.length - 1].endJD - parent.endJD;
    if (Math.abs(s) > epsilon || Math.abs(e) > epsilon) edge = { start: s, end: e };
  }
  return { ok: !gaps.length && !overlaps.length && !edge, gaps, overlaps, edge };
}
